/**
 * Displays immutable Vault versions and the operations allowed on each one.
 *
 * Users must distinguish current metadata from historical file content. This
 * dialog owns that presentation while download and new-version operations stay
 * behind their authorized service contracts.
 */
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Download, FileClock, FileUp } from 'lucide-react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from '@/components/ui'
import { formatDate, formatSize } from '../lib/format'
import { downloadFileVersion, listFileVersions } from '../services/vault-api'
import { triggerBrowserDownload } from '../services/browser-download'
import type { ItemResponse } from '../types'

interface VaultVersionHistoryDialogProps {
  api: NodeApiClient
  orgId: string
  vaultId: string
  entry: ItemResponse | null
  onClose: () => void
  onUploadVersion: (entry: ItemResponse) => void
}

export function VaultVersionHistoryDialog({
  api,
  orgId,
  vaultId,
  entry,
  onClose,
  onUploadVersion,
}: VaultVersionHistoryDialogProps) {
  const versions = useQuery({
    queryKey: ['vault-versions', orgId, vaultId, entry?.item.itemId],
    queryFn: () => listFileVersions(api, orgId, vaultId, entry!.item.itemId),
    enabled: Boolean(entry),
  })

  const download = async (versionId: string) => {
    if (!entry) return
    try {
      const url = await downloadFileVersion(
        api,
        orgId,
        vaultId,
        entry.item.itemId,
        versionId,
      )
      triggerBrowserDownload(url)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? 'Version download failed')
    }
  }

  return (
    <Dialog open={Boolean(entry)} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="fixed inset-y-0 left-auto right-0 flex h-dvh w-full max-w-md flex-col gap-0 rounded-none border-y-0 border-r-0 p-0 sm:max-w-md data-[state=open]:slide-in-from-right-full data-[state=open]:zoom-in-100 data-[state=closed]:slide-out-to-right-full data-[state=closed]:zoom-out-100">
        <DialogHeader className="border-b p-6 pr-12">
          <DialogTitle className="flex items-center gap-2">
            <FileClock className="h-5 w-5" /> Version history
          </DialogTitle>
          <DialogDescription>{entry?.item.name}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="min-h-0 flex-1">
          {versions.isPending ? (
            <p className="p-6 text-sm text-muted-foreground">Loading versions…</p>
          ) : versions.isError ? (
            <div className="flex items-center gap-3 p-6 text-sm text-destructive">
              <AlertCircle className="h-5 w-5" /> Version history could not be loaded.
            </div>
          ) : versions.data?.map((version, index) => {
            const current = version.versionId === entry?.item.currentVersionId
            const versionNumber = versions.data.length - index
            return (
              <div
                key={version.versionId}
                className={`m-4 flex items-center gap-3 rounded-xl border p-4 ${current ? 'border-primary bg-primary/5' : ''}`}
              >
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base font-semibold ${current ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  v{versionNumber}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {current ? 'Current version' : `Version ${versionNumber}`}
                    {current && <Badge>Current</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Uploaded by {version.uploadedBy}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(version.createdAt)} · {formatSize(version.sizeBytes)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Download version ${versionNumber}`}
                  onClick={() => void download(version.versionId)}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
          {!versions.isPending && !versions.data?.length && (
            <p className="p-5 text-sm text-muted-foreground">No completed versions.</p>
          )}
        </ScrollArea>
        {entry && !entry.item.readOnly && ['editor', 'owner'].includes(entry.effectiveRole) && (
          <DialogFooter className="border-t p-4">
            <Button variant="outline" onClick={() => onUploadVersion(entry)}>
              <FileUp className="mr-2 h-4 w-4" /> Upload new version
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
